import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getFriendlyApiMessage, requestApiJson } from "@/lib/api-client";

export const TemplateManager = ({ isOpen, onClose, onApplyTemplate, currentConfig }) => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const data = await requestApiJson("/templates");
      setTemplates(data?.templates || []);
    } catch (error) {
      console.error("Fehler beim Laden der Vorlagen:", error);
    }
    setIsLoading(false);
  };

  const saveTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error("Bitte geben Sie einen Namen ein");
      return;
    }

    setIsSaving(true);
    try {
      await requestApiJson("/templates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newTemplateName,
          title: currentConfig.title,
          checklist_enabled: currentConfig.checklistEnabled,
          checklist: currentConfig.checklist,
        }),
      });

      toast.success("Vorlage gespeichert");
      setNewTemplateName("");
      loadTemplates();
    } catch (error) {
      toast.error(getFriendlyApiMessage(error));
    }
    setIsSaving(false);
  };

  const deleteTemplate = async (templateId) => {
    try {
      await requestApiJson(`/templates/${templateId}`, {
        method: "DELETE",
      });

      toast.success("Vorlage gelöscht");
      loadTemplates();
    } catch (error) {
      toast.error(getFriendlyApiMessage(error));
    }
  };

  const applyTemplate = (template) => {
    onApplyTemplate({
      title: template.title,
      checklistEnabled: template.checklist_enabled,
      checklist: template.checklist,
    });
    toast.success(`Vorlage "${template.name}" angewendet`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="template-manager-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Vorlagen verwalten
          </DialogTitle>
        </DialogHeader>

        {/* Save new template */}
        <div className="p-4 bg-slate-50 rounded-lg border">
          <Label className="text-sm font-medium mb-2 block">Aktuelle Konfiguration als Vorlage speichern</Label>
          <div className="flex gap-2">
            <Input
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="Vorlagenname eingeben..."
              data-testid="template-name-input"
            />
            <Button onClick={saveTemplate} disabled={isSaving} data-testid="save-template-btn">
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Templates list */}
        <div className="mt-4">
          <Label className="text-sm font-medium mb-2 block">Gespeicherte Vorlagen</Label>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Keine Vorlagen vorhanden
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between p-3 bg-white border rounded-lg hover:border-blue-300 transition-colors"
                  data-testid={`template-${template.id}`}
                >
                  <div className="flex-1">
                    <div className="font-medium text-sm">{template.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Titel: {template.title}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => applyTemplate(template)}
                      data-testid={`apply-template-${template.id}`}
                    >
                      Anwenden
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteTemplate(template.id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-template-${template.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateManager;
